"use client";

import { motion } from "framer-motion";
import { ArrowUp, ArrowDown } from "lucide-react";
import { useState } from "react";

import type { Locale } from "./templates/ServicePageTemplate";

const peT = {
  sv: { title: "Prisuppskattning", pickupFrom: "Hämta från", pickupPlaceholder: "Upphämtningsadress", deliverTo: "Flytta till", deliverPlaceholder: "Avlämningsadress", cta: "Se priser", defaultSubtitle: "Ange dina adresser för att se priser och boka din bortforsling" },
  en: { title: "Price estimate", pickupFrom: "Pick up from", pickupPlaceholder: "Pickup address", deliverTo: "Deliver to", deliverPlaceholder: "Drop-off address", cta: "See prices", defaultSubtitle: "Enter your addresses to see prices and book your removal" },
};

interface PriceEstimateProps {
  subtitle?: string;
  locale?: Locale;
  pickupLabel?: string;
  pickupPlaceholder?: string;
  hideDropoff?: boolean;
  bookingHref?: string;
}

export default function PriceEstimate({
  subtitle,
  locale = "sv",
  pickupLabel,
  pickupPlaceholder,
  hideDropoff,
  bookingHref = "/boka",
}: PriceEstimateProps = {}) {
  const l = peT[locale];
  const resolvedSubtitle = subtitle ?? l.defaultSubtitle;
  const [pickup, setPickup] = useState("");
  const [dropoff, setDropoff] = useState("");

  return (
    <section className="py-6 sm:pt-10 sm:pb-24 bg-white">
      <div className="mx-auto max-w-screen-2xl sm:px-6 xl:px-14">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="rounded-none sm:rounded-2xl px-6 py-6 sm:px-10 sm:py-7 lg:px-12 lg:py-8"
          style={{ background: "linear-gradient(135deg, #2a4a63 0%, #1d3547 100%)" }}
        >
          <div className="flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-12">
            {/* Left — text */}
            <div className="lg:w-[30%] shrink-0">
              <h2 className="text-2xl sm:text-3xl font-bold text-white leading-tight">
                {l.title}
              </h2>
              <p className="mt-1.5 text-sm sm:text-base text-white/75 leading-relaxed">
                {resolvedSubtitle}
              </p>
            </div>

            {/* Right — form */}
            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row gap-3">
                {/* Pickup */}
                <div className="flex items-center gap-3 flex-1 rounded-xl bg-white/15 backdrop-blur-sm px-4 py-3 border border-white/10">
                  <ArrowUp className="h-4 w-4 text-white/70 shrink-0" />
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="text-[10px] font-medium text-white/60 uppercase tracking-wide">
                      {pickupLabel ?? l.pickupFrom}
                    </span>
                    <input
                      type="text"
                      value={pickup}
                      onChange={(e) => setPickup(e.target.value)}
                      placeholder={pickupPlaceholder ?? l.pickupPlaceholder}
                      className="w-full bg-transparent text-white placeholder:text-white/40 outline-none text-sm mt-0.5"
                    />
                  </div>
                </div>

                {/* Drop-off */}
                {!hideDropoff && (
                <div className="flex items-center gap-3 flex-1 rounded-xl bg-white/15 backdrop-blur-sm px-4 py-3 border border-white/10">
                  <ArrowDown className="h-4 w-4 text-white/70 shrink-0" />
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="text-[10px] font-medium text-white/60 uppercase tracking-wide">
                      {l.deliverTo}
                    </span>
                    <input
                      type="text"
                      value={dropoff}
                      onChange={(e) => setDropoff(e.target.value)}
                      placeholder={l.deliverPlaceholder}
                      className="w-full bg-transparent text-white placeholder:text-white/40 outline-none text-sm mt-0.5"
                    />
                  </div>
                </div>
                )}
              </div>

              {/* CTA button */}
              <a href={bookingHref} className="mt-3 w-full rounded-xl bg-brand-secondary hover:brightness-110 text-white font-bold text-sm sm:text-base py-3 transition-all cursor-pointer flex flex-col items-center">
                <span>{l.cta}</span>
                <span className="text-sm font-bold text-brand-primary ">50% RUT</span>
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
